import { useTranslations } from '@vocab/react';
import { useEffect } from 'react';

import { UpsellPanelV2 } from 'src/app/shared/components/UpsellPanel/upsellPanelv2';
import type { PremiumUpsellStorageKey } from 'src/app/shared/constants/storage';
import {
  PREMIUM_UPSELL_DISPLAYED,
  PREMIUM_UPSELL_PRESSED,
  UPSELL_DISPLAYED,
  UPSELL_PRESSED,
} from 'src/app/shared/dataAnalytics/schema/constants';
import { saveTrackingData } from 'src/app/shared/dataAnalytics/storage';
import { useProductSetFromCart } from 'src/app/shared/hooks/useProductSetFromCart';
import { useQueryParam } from 'src/app/shared/hooks/useQueryParam';
import { useShoppingCart } from 'src/app/shared/hooks/useShoppingCart';
import { getUpsellAndBaseProductPriceDifference } from 'src/app/utils/getUpsellAndBaseProductPriceDifference';
import { useAnalyticsTracking } from 'src/app/v2/hooks/useAnalyticsTracking';
import { useHirerAccount } from 'src/app/v2/hooks/useHirerAccount';
import { useHubbleTrackEventHandler } from 'src/app/v2/hooks/useHubbleTrackEventHandler';
import type { CreateJobMutation } from 'src/graphql/generated';

import translations from './.vocab';
interface UpsellSectionProps {
  job: NonNullable<CreateJobMutation['createJob']>;
  sessionStorageKey: PremiumUpsellStorageKey;
  baseProductId: string;
  upsellProductId: string;
}

export const PremiumUpsell: React.FC<UpsellSectionProps> = ({
  job,
  sessionStorageKey,
  baseProductId,
  upsellProductId,
}) => {
  useEffect(() => {
    sessionStorage.setItem(sessionStorageKey, 'true');
  }, [sessionStorageKey]);

  const { trackingAdapter } = useAnalyticsTracking();
  const { sendHubbleTrackEvent } = useHubbleTrackEventHandler();
  const {
    cartState: { items = [] } = {},
    addLineItem,
    assignJobIdAndRevisionToParentProduct,
    cartProductInProgress,
  } = useShoppingCart();
  const draftId = useQueryParam('draftid');
  const productSet = useProductSetFromCart({ draftId });
  const hirerAccount = useHirerAccount();

  const isUpsellProductSelected = items.find(
    (item) => item.product.id === upsellProductId,
  );

  const { price, tax } = getUpsellAndBaseProductPriceDifference({
    baseProductId,
    upsellProductId,
    productSet,
    hidePrice: hirerAccount?.hidePrice,
  });
  const { jobId, revisionId } = job;

  // @ts-ignore
  const isJobIdPresent = items.find((item) => Boolean(item.product?.job?.id));

  const upsellProductPrice = productSet?.items?.find(
    (item) => item?.id === upsellProductId,
  )?.price;

  const upsellProductNetPrice =
    !hirerAccount?.hidePrice && upsellProductPrice?.__typename === 'Price'
      ? upsellProductPrice.netPrice
      : undefined;

  useEffect(() => {
    trackingAdapter.sendLinkEvent({
      eventName: PREMIUM_UPSELL_DISPLAYED,
    });
    sendHubbleTrackEvent(UPSELL_DISPLAYED, {
      eventName: UPSELL_DISPLAYED,
      widgetName: 'upsellWidget',
      products: [
        { productId: upsellProductId, netPrice: upsellProductNetPrice },
      ],
    });
    saveTrackingData({ isPremiumAdOffered: true });
  }, [
    trackingAdapter,
    sendHubbleTrackEvent,
    upsellProductId,
    upsellProductNetPrice,
  ]);

  useEffect(() => {
    // This is a side effect of shopping cart not allowing job ID to be set before cart is updated
    // When cart is updated, job ID will be missing because a new product added
    if (!isJobIdPresent) {
      assignJobIdAndRevisionToParentProduct({
        jobId,
        revisionId,
      });
    }
  }, [
    isJobIdPresent,
    jobId,
    revisionId,
    assignJobIdAndRevisionToParentProduct,
  ]);

  const onButtonClicked = () => {
    const eventData = {
      eventName: PREMIUM_UPSELL_PRESSED,
      linkAction: isUpsellProductSelected ? 'remove' : 'select',
      premiumUpgradePrice: price,
    };

    trackingAdapter.sendLinkEvent(eventData);
    sendHubbleTrackEvent(UPSELL_PRESSED, {
      ...eventData,
      eventName: UPSELL_PRESSED,
      widgetName: 'upsellWidget',
      productId: upsellProductId,
      selectedProductId: baseProductId,
    });

    addLineItem({
      productId: isUpsellProductSelected ? baseProductId : upsellProductId,
      productType: 'AdProduct',
    });
  };

  const { t } = useTranslations(translations);

  return (
    <UpsellPanelV2
      heading={t('premium-upsell-heading')}
      description={t('premium-upsell-panel-description')}
      benefits={[
        t('premium-upsell-selling-point-1'),
        t('premium-upsell-selling-point-2'),
      ]}
      price={price}
      tax={tax || undefined}
      onClick={onButtonClicked}
      isSelected={Boolean(isUpsellProductSelected)}
      loading={Boolean(Object.keys(cartProductInProgress).length)}
    />
  );
};
